import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  ChevronDown,
  ChevronUp,
  Download,
  FolderOpen,
  RefreshCw,
  Trash2,
  MapPin,
  Phone,
  Instagram,
  User,
  CalendarDays,
  ArrowUpRight,
} from 'lucide-react';
import { CommercialData, FieldResearch, ResearchResponse } from '../../types/fieldResearch';
import { getResearches, getResponses, saveResponses } from '../../utils/storage';
import { exportarJson, selecionarArquivo } from '../../utils/jsonFileService';

function InterestBadge({ level }: { level: CommercialData['interestLevel'] }) {
  const map = {
    alto: 'bg-green-500/20 text-green-300 border-green-500/30',
    medio: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    baixo: 'bg-red-500/20 text-red-300 border-red-500/30',
  };
  const label = { alto: '🔥 Alto', medio: '⚡ Médio', baixo: '❄️ Baixo' };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${map[level]}`}>
      {label[level]}
    </span>
  );
}

function VisitBadge({ status }: { status: CommercialData['visitStatus'] }) {
  const map: Record<string, string> = {
    visitado: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    interessado: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    sem_interesse: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    retornar_depois: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  };
  const labels: Record<string, string> = {
    visitado: 'Visitado',
    interessado: 'Interessado',
    sem_interesse: 'Sem interesse',
    retornar_depois: 'Retornar depois',
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
        map[status] || 'bg-white/5 text-white/30 border-white/10'
      }`}
    >
      {labels[status] || status}
    </span>
  );
}

export default function AdminRegistrosPage() {
  const [researches] = useState<FieldResearch[]>(getResearches);
  const [responses, setResponses] = useState<ResearchResponse[]>(getResponses);
  const [filter, setFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Re-lê o localStorage sempre que montar ou ao forçar refresh
  useEffect(() => {
    setResponses(getResponses());
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const persist = (data: ResearchResponse[]) => {
    saveResponses(data);
    setResponses(data);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Excluir este registro?')) return;
    persist(responses.filter((r) => r.id !== id));
  };

  // Mescla importados com os existentes (evita duplicatas por id)
  const handleLoadFile = async () => {
    const imported = await selecionarArquivo();
    if (!imported.length) return;
    const existing = getResponses();
    const existingIds = new Set(existing.map((r) => r.id));
    const merged = [...existing, ...imported.filter((r) => !existingIds.has(r.id))];
    persist(merged);
  };

  const researchLabel = (id: string) => researches.find((r) => r.id === id)?.name ?? id;

  const filtered = filter ? responses.filter((r) => r.researchId === filter) : responses;
  const altoInteresse = filtered.filter((r) => r.commercial.interestLevel === 'alto').length;
  const retornar = filtered.filter((r) => r.commercial.visitStatus === 'retornar_depois').length;

  return (
    <div className="min-h-full bg-[#07051a] text-white">
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">

        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-white">Registros de Campo</h1>
            <p className="text-xs text-white/30 mt-0.5">
              Histórico de visitas realizadas — {responses.length} registro{responses.length !== 1 ? 's' : ''} no total
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              title="Atualizar"
              className="p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white/40 hover:text-white transition"
            >
              <RefreshCw size={14} />
            </button>
            <button
              onClick={handleLoadFile}
              className="flex items-center gap-2 px-3 py-2.5 bg-white/5 border border-white/10 hover:bg-white/10 text-white/50 hover:text-white text-xs font-semibold rounded-xl transition"
            >
              <FolderOpen size={13} />
              Importar JSON
            </button>
            {responses.length > 0 && (
              <button
                onClick={() => exportarJson(responses)}
                className="flex items-center gap-2 px-3 py-2.5 bg-green-600/15 border border-green-500/25 hover:bg-green-600/25 text-green-300 text-xs font-semibold rounded-xl transition"
              >
                <Download size={13} />
                Exportar
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total', value: filtered.length, color: 'text-white', bg: 'from-purple-600/15 to-violet-600/10 border-purple-500/20', icon: BarChart3 },
            { label: 'Alto interesse', value: altoInteresse, color: 'text-green-300', bg: 'from-green-600/15 to-emerald-600/10 border-green-500/20', icon: ArrowUpRight },
            { label: 'Retornar', value: retornar, color: 'text-orange-300', bg: 'from-orange-600/15 to-orange-600/10 border-orange-500/20', icon: CalendarDays },
          ].map(({ label, value, color, bg, icon: Icon }) => (
            <div key={label} className={`bg-gradient-to-br ${bg} border rounded-2xl px-5 py-4`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-white/35 font-medium">{label}</span>
                <Icon size={13} className={color} />
              </div>
              <p className={`text-3xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Filter pills */}
        {responses.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilter('')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                !filter
                  ? 'bg-purple-600/20 border-purple-500/40 text-purple-300'
                  : 'bg-white/4 border-white/10 text-white/35 hover:bg-white/8'
              }`}
            >
              Todos ({responses.length})
            </button>
            {researches.map((r) => {
              const count = responses.filter((res) => res.researchId === r.id).length;
              if (count === 0) return null;
              return (
                <button
                  key={r.id}
                  onClick={() => setFilter(r.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    filter === r.id
                      ? 'bg-purple-600/20 border-purple-500/40 text-purple-300'
                      : 'bg-white/4 border-white/10 text-white/35 hover:bg-white/8'
                  }`}
                >
                  {r.name} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Section divider */}
        <div className="flex items-center gap-3">
          <h2 className="text-xs font-semibold text-white/25 uppercase tracking-widest whitespace-nowrap">
            {filter ? researchLabel(filter) : 'Todos os registros'}
          </h2>
          <div className="flex-1 h-px bg-white/5" />
        </div>

        {/* List */}
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-white/[0.03] border border-dashed border-white/10 rounded-2xl py-20 text-center">
              <BarChart3 size={36} className="mx-auto mb-4 text-white/10" />
              <p className="text-white/30 text-sm font-medium">Nenhum registro encontrado</p>
              <p className="text-white/15 text-xs mt-1">
                Colete dados em{' '}
                <a href="/pesquisa-campo" className="text-purple-400 hover:underline">
                  /pesquisa-campo
                </a>{' '}
                ou importe um arquivo JSON
              </p>
            </div>
          ) : (
            filtered
              .slice()
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .map((r) => (
                <div
                  key={r.id}
                  className="group bg-white/[0.04] hover:bg-white/[0.06] border border-white/8 hover:border-white/15 rounded-2xl overflow-hidden transition-all duration-200"
                >
                  {/* Card header */}
                  <button
                    type="button"
                    className="w-full text-left px-5 py-4 flex items-center gap-4"
                    onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                  >
                    {/* Status dot */}
                    <div className={`shrink-0 w-2 h-2 rounded-full ${
                      r.commercial.interestLevel === 'alto'
                        ? 'bg-green-400 shadow-[0_0_8px_#34d39966]'
                        : r.commercial.interestLevel === 'medio'
                        ? 'bg-yellow-400'
                        : 'bg-red-400/60'
                    }`} />

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {r.company.name || 'Estabelecimento sem nome'}
                      </p>
                      <p className="text-xs text-white/30 mt-0.5">
                        <span className="text-white/50">{researchLabel(r.researchId)}</span>
                        {r.company.city ? ` · ${r.company.city}` : ''}
                        {r.company.state ? `/${r.company.state}` : ''}
                        {' · '}
                        {new Date(r.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <InterestBadge level={r.commercial.interestLevel} />
                      {expandedId === r.id ? (
                        <ChevronUp size={14} className="text-white/25" />
                      ) : (
                        <ChevronDown size={14} className="text-white/25" />
                      )}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {expandedId === r.id && (
                    <div className="px-5 pb-5 border-t border-white/8 space-y-4 pt-4">
                      {/* Badges */}
                      <div className="flex flex-wrap gap-2">
                        <VisitBadge status={r.commercial.visitStatus} />
                        {r.company.usesSystem === 'sim' && (
                          <span className="px-2 py-0.5 rounded-full text-xs border bg-purple-500/15 border-purple-500/30 text-purple-300">
                            🖥️ {r.company.systemName || 'Sistema'}
                            {r.company.systemPrice ? ` · R$ ${r.company.systemPrice}` : ''}
                          </span>
                        )}
                        {r.company.usesSystem === 'nao' && (
                          <span className="px-2 py-0.5 rounded-full text-xs border bg-white/5 border-white/10 text-white/25">
                            Sem sistema
                          </span>
                        )}
                      </div>

                      {/* Info grid */}
                      <div className="grid grid-cols-2 gap-3">
                        {r.company.contactName && (
                          <div className="flex items-center gap-2 text-xs text-white/45">
                            <User size={11} className="text-white/25 shrink-0" />
                            {r.company.contactName}
                          </div>
                        )}
                        {r.company.phone && (
                          <div className="flex items-center gap-2 text-xs text-white/45">
                            <Phone size={11} className="text-white/25 shrink-0" />
                            {r.company.phone}
                          </div>
                        )}
                        {r.company.instagram && (
                          <div className="flex items-center gap-2 text-xs text-white/45">
                            <Instagram size={11} className="text-white/25 shrink-0" />
                            {r.company.instagram}
                          </div>
                        )}
                        {r.company.address && (
                          <div className="flex items-center gap-2 text-xs text-white/45 col-span-2">
                            <MapPin size={11} className="text-white/25 shrink-0" />
                            {r.company.address}
                            {r.company.number ? `, ${r.company.number}` : ''}
                            {r.company.district ? ` — ${r.company.district}` : ''}
                            {r.company.city ? ` · ${r.company.city}` : ''}
                            {r.company.state ? `/${r.company.state}` : ''}
                          </div>
                        )}
                        {r.location.lat !== null && (
                          <div className="flex items-center gap-2 text-xs text-white/30 col-span-2">
                            <MapPin size={11} className="text-purple-400/50 shrink-0" />
                            GPS: {r.location.lat.toFixed(5)}, {r.location.lng?.toFixed(5)}
                          </div>
                        )}
                      </div>

                      {/* Próximo passo */}
                      {r.commercial.nextStep && (
                        <div className="bg-white/3 border border-white/8 rounded-xl px-4 py-3">
                          <p className="text-[10px] text-white/25 uppercase tracking-wider font-semibold mb-1">Próximo passo</p>
                          <p className="text-xs text-white/50 italic">{r.commercial.nextStep}</p>
                        </div>
                      )}

                      {/* Respostas do formulário */}
                      {Object.keys(r.answers).length > 0 && (
                        <div className="bg-white/3 border border-white/8 rounded-xl px-4 py-3 space-y-2">
                          <p className="text-[10px] text-white/25 uppercase tracking-wider font-semibold">Respostas</p>
                          {Object.entries(r.answers).map(([key, val]) => (
                            <div key={key} className="flex gap-2 text-xs">
                              <span className="text-white/25 shrink-0">{key}:</span>
                              <span className="text-white/50">{Array.isArray(val) ? val.join(', ') : String(val)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => handleDelete(r.id)}
                        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-red-600/10 border border-red-500/20 text-red-400 text-xs font-medium hover:bg-red-600/20 transition"
                      >
                        <Trash2 size={12} />
                        Excluir registro
                      </button>
                    </div>
                  )}
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
}

